import { useId, useState } from 'react';
import { mountCandidates, type Motor, type Rocket } from '@openrocket-web/physics';
import type { Action } from '../state/app';
import { assignmentLabel, configLabel, mountsOf, type MotorConfig } from '../state/configs';
import { findMotor } from '../state/motors';
import { parseMotorFile } from '../state/motors';
import { readText } from './readFile';
import type { Fmt } from './format';
import { Icon } from './icons';
import { Tabs } from './Tabs';

type SubTab = 'motors' | 'recovery' | 'stages';

const SUBTABS: ReadonlyArray<{ id: SubTab; label: string; disabled?: boolean }> = [
  { id: 'motors', label: 'Motores' },
  { id: 'recovery', label: 'Recuperación' },
  { id: 'stages', label: 'Etapas', disabled: true },
];

interface TabProps {
  readonly rocket: Rocket;
  readonly motors: readonly Motor[];
  readonly configs: readonly MotorConfig[];
  readonly activeId: string;
  readonly dispatch: (a: Action) => void;
  /** Abre el diálogo para elegir el motor de un soporte en una configuración. */
  readonly onPick: (configId: string, mountId: string) => void;
  readonly onRename: (configId: string) => void;
  /** Lleva al diseño y abre las propiedades de un componente. */
  readonly onEditComponent: (id: string) => void;
}

const DEPLOYMENT = { ejection: 'Carga de eyección del motor', apogee: 'En el apogeo', altitude: 'A una altitud (en descenso)' } as const;

/** Pestaña «Configuraciones»: las configuraciones de motor del diseño, con sus motores por soporte. */
export function ConfigsTab({ rocket, motors, configs, activeId, dispatch, onPick, onRename, onEditComponent }: TabProps): JSX.Element {
  const [sub, setSub] = useState<SubTab>('motors');
  const [mountId, setMountId] = useState<string | undefined>();
  const mounts = mountsOf(rocket);
  const mount = mounts.find((m) => m.id === mountId) ?? mounts[0];
  const active = configs.find((c) => c.id === activeId) ?? configs[0]!;
  const assigned = mount === undefined ? undefined : active.motors[mount.id];
  const tubes = mountCandidates(rocket);
  const chutes = rocket.stack.flatMap((c) => (c.type === 'nose-cone' ? [] : c.children.flatMap((k) => (k.type === 'parachute' ? [k] : []))));

  return (
    <section aria-labelledby="configs-title" className="tabpage cfgtab">
      <h2 id="configs-title" className="sr-only">
        Configuraciones del motor
      </h2>
      <div className="bigbar" role="toolbar" aria-label="Acciones sobre las configuraciones">
        <button type="button" onClick={() => dispatch({ type: 'config-add' })}>
          <Icon name="tool-new" />
          Nueva configuración
        </button>
        <button type="button" onClick={() => onRename(active.id)}>
          <Icon name="tool-rename" />
          Renombrar configuración
        </button>
        <button type="button" onClick={() => dispatch({ type: 'config-remove', id: active.id })}>
          <Icon name="tool-delete" />
          Quitar configuración
        </button>
        <button type="button" onClick={() => dispatch({ type: 'config-duplicate', id: active.id })}>
          <Icon name="tool-copy" />
          Duplicar
        </button>
      </div>

      <Tabs tabs={SUBTABS} value={sub} onChange={setSub} idPrefix="cfg" label="Tipo de configuración" />

      <div role="tabpanel" id="cfg-panel" aria-labelledby={`cfg-tab-${sub}`} className="cfgtab__panel">
        {sub === 'motors' && (
          <>
            <fieldset className="box box--mounts">
              <legend>Soportes de motor</legend>
              <ul className="mountlist" aria-label="Componentes que pueden alojar un motor">
                {tubes.map((t) => (
                  <li key={t.id}>
                    <label>
                      <input type="checkbox" checked={t.motorMount !== undefined} onChange={(e) => dispatch({ type: 'mount-set', id: t.id, on: e.target.checked })} />
                      {t.name}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>

            <fieldset className="box box--configs">
              <legend>Configuraciones del motor</legend>
              <div className="region region--table">
                <table className="grid grid--configs" aria-label="Configuraciones del motor">
                  <thead>
                    <tr>
                      <th scope="col">Configuración</th>
                      {mounts.map((m) => (
                        <th scope="col" key={m.id}>
                          {m.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((c) => {
                      const on = c.id === active.id;
                      return (
                        <tr key={c.id} className={on ? 'is-active' : undefined} onClick={() => dispatch({ type: 'config-select', id: c.id })}>
                          <th scope="row">
                            <button type="button" className="grid__select" aria-current={on ? 'true' : undefined} onClick={() => dispatch({ type: 'config-select', id: c.id })}>
                              {configLabel(c, rocket, motors)}
                            </button>
                          </th>
                          {mounts.map((m) => {
                            const a = c.motors[m.id];
                            const here = on && mount?.id === m.id;
                            return (
                              <td key={m.id} className={here ? 'is-cell' : undefined}>
                                <button
                                  type="button"
                                  className="grid__select grid__select--cell"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch({ type: 'config-select', id: c.id });
                                    setMountId(m.id);
                                  }}
                                  onDoubleClick={() => onPick(c.id, m.id)}
                                >
                                  <span className="chip">{a === undefined ? 'Ninguno' : assignmentLabel(a, motors)}</span>
                                  <span className="chip chip--auto">Automático</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="cfg-actions">
                <button type="button" disabled={mount === undefined} onClick={() => mount !== undefined && onPick(active.id, mount.id)}>
                  Seleccionar motor
                </button>
                <button type="button" disabled={assigned === undefined} onClick={() => mount !== undefined && dispatch({ type: 'config-set-motor', id: active.id, mountId: mount.id })}>
                  Quitar motor
                </button>
                <button type="button" disabled title="El simulador usa siempre la ignición automática">
                  Seleccionar ignición
                </button>
                <button type="button" disabled title="El simulador usa siempre la ignición automática">
                  Reiniciar ignición
                </button>
              </div>
            </fieldset>
          </>
        )}

        {sub === 'recovery' && (
          <fieldset className="box box--wide">
            <legend>Dispositivos de recuperación</legend>
            <div className="region region--table">
              {chutes.length === 0 ? (
                <p className="muted pad">El diseño no tiene paracaídas.</p>
              ) : (
                <table className="grid" aria-label="Dispositivos de recuperación">
                  <thead>
                    <tr>
                      <th scope="col">Dispositivo</th>
                      <th scope="col">Despliegue</th>
                      <th scope="col">Altitud</th>
                      <th scope="col">
                        <span className="sr-only">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {chutes.map((k) => (
                      <tr key={k.id}>
                        <th scope="row">{k.name}</th>
                        <td>{DEPLOYMENT[k.deployment.event]}</td>
                        <td>{k.deployment.event === 'altitude' ? `${Math.round(k.deployment.altitude)} m` : '—'}</td>
                        <td>
                          <button type="button" onClick={() => onEditComponent(k.id)}>
                            Editar componente
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p className="muted small">El despliegue se define en el propio componente y vale para todas las configuraciones.</p>
          </fieldset>
        )}
      </div>
    </section>
  );
}

/** Carga de archivos de curva de empuje. */
function MotorFiles({ dispatch }: { readonly dispatch: (a: Action) => void }): JSX.Element {
  const inputId = useId();
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [loaded, setLoaded] = useState<readonly string[]>([]);

  async function onFiles(files: FileList | null): Promise<void> {
    if (files === null) return;
    const found: Motor[] = [];
    const problems: string[] = [];
    for (const file of Array.from(files)) {
      const r = parseMotorFile(file.name, await readText(file));
      found.push(...r.motors);
      for (const e of r.errors) problems.push(`${file.name}${e.motor === undefined ? '' : ` (${e.motor})`}${e.line === undefined ? '' : `, línea ${e.line}`}: ${e.message}`);
    }
    if (found.length > 0) {
      dispatch({ type: 'add-motors', motors: found });
      dispatch({ type: 'notice', level: 'info', text: found.length === 1 ? 'Se cargó 1 motor.' : `Se cargaron ${found.length} motores.` });
    }
    setLoaded(found.map((m) => `${m.manufacturer} ${m.designation}`));
    setErrors(problems);
  }

  return (
    <div className="motorfiles">
      <p className="muted small">
        Carga archivos de curva de empuje <code>.eng</code> (RASP) o <code>.rse</code> (RockSim). Los archivos se leen en tu equipo y no se envían a ningún sitio.
      </p>
      <div className="field">
        <label htmlFor={inputId}>Cargar archivos de motor</label>
        <input id={inputId} type="file" accept=".eng,.rse" multiple onChange={(e) => void onFiles(e.target.files)} />
      </div>
      {loaded.length > 0 && (
        <ul className="issue-list" aria-label="Motores cargados">
          {loaded.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <ul className="issue-list issue-list--error" role="alert" aria-label="Errores al leer motores">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface PickerProps {
  readonly motors: readonly Motor[];
  readonly fmt: Fmt;
  /** Motor que lleva ahora el soporte en esa configuración. */
  readonly current: { readonly motorId: string; readonly delay: number } | undefined;
  readonly mountName: string;
  readonly dispatch: (a: Action) => void;
  readonly onAccept: (motorId: string, delay: number) => void;
}

/** Contenido del diálogo «Seleccionar motor»: la biblioteca, el retardo y la carga de archivos. */
export function MotorPicker({ motors, fmt, current, mountName, dispatch, onAccept }: PickerProps): JSX.Element {
  const [chosen, setChosen] = useState<string | undefined>(() => (current === undefined ? motors[0]?.id : findMotor(motors, current.motorId)?.id));
  const motor = motors.find((m) => m.id === chosen);
  const delays = motor === undefined || motor.plugged ? [] : motor.delays;
  const [delay, setDelay] = useState<number>(current?.delay ?? 0);
  const effective = delays.length === 0 ? 0 : delays.includes(delay) ? delay : delays[0]!;
  const delayId = useId();

  return (
    <div className="picker">
      <p className="muted small">Soporte: {mountName}</p>
      <div className="region region--table region--picker">
        <table className="grid" aria-label="Motores disponibles">
          <thead>
            <tr>
              <th scope="col">Motor</th>
              <th scope="col">Clase</th>
              <th scope="col">Impulso total</th>
              <th scope="col">Combustión</th>
              <th scope="col">Diámetro</th>
              <th scope="col">Longitud</th>
            </tr>
          </thead>
          <tbody>
            {motors.map((m) => {
              const on = m.id === chosen;
              return (
                <tr key={m.id} className={on ? 'is-active' : undefined} onClick={() => setChosen(m.id)} onDoubleClick={() => onAccept(m.id, m.plugged || m.delays.length === 0 ? 0 : m.delays.includes(delay) ? delay : m.delays[0]!)}>
                  <th scope="row">
                    <button type="button" className="grid__select" aria-current={on ? 'true' : undefined} onClick={() => setChosen(m.id)}>
                      {m.manufacturer} {m.designation}
                    </button>
                  </th>
                  <td>{m.impulseClass}</td>
                  <td>{m.totalImpulse.toFixed(1)} N·s</td>
                  <td>{fmt.seconds(m.burnTime, 3)}</td>
                  <td>{fmt.dimension(m.diameter, 3)}</td>
                  <td>{fmt.dimension(m.length, 3)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="picker__row">
        <div className="field">
          <label htmlFor={delayId}>Retardo de eyección (s)</label>
          <select id={delayId} value={String(effective)} disabled={delays.length === 0} onChange={(e) => setDelay(Number(e.target.value))}>
            {delays.length === 0 ? <option value="0">Sin retardo</option> : delays.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <button type="button" className="primary" disabled={motor === undefined} onClick={() => motor !== undefined && onAccept(motor.id, effective)}>
          Aceptar
        </button>
        <button type="button" className="danger" disabled={motor === undefined} onClick={() => motor !== undefined && dispatch({ type: 'remove-motor', id: motor.id })}>
          Quitar de la biblioteca
        </button>
      </div>

      <MotorFiles dispatch={dispatch} />
    </div>
  );
}

/** Contenido del diálogo «Renombrar configuración». */
export function RenameConfig({ name, onSubmit }: { readonly name: string; readonly onSubmit: (name: string) => void }): JSX.Element {
  const [draft, setDraft] = useState(name);
  const id = useId();
  return (
    <form
      className="rename"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft);
      }}
    >
      <div className="field">
        <label htmlFor={id}>Nombre de la configuración</label>
        <input id={id} type="text" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onFocus={(e) => e.currentTarget.select()} />
        <span className="field__hint">Si lo dejas vacío se usa el nombre automático, con los motores.</span>
      </div>
      <div className="toolbar">
        <button type="submit" className="primary">
          Aceptar
        </button>
      </div>
    </form>
  );
}
